/**
 * The account duck. Default export is the reducer
 *
 * Action Types:
 * - ACCOUNT_DRAWER_CLOSED
 * - ACCOUNT_DRAWER_OPENED
 * - ACCOUNT_FETCH_FAILED
 * - ACCOUNT_FETCH_REQUESTED
 * - ACCOUNT_FETCH_SUCCEEDED
 * - LOG_IN_FAILED
 * - LOG_IN_REQUESTED
 * - LOG_IN_SUCCEEDED
 * - LOG_OUT_REQUESTED
 * - RESET_PASSWORD_FAILED
 * - RESET_PASSWORD_REQUESTED
 * - RESET_PASSWORD_SUCCEEDED
 * - SIGN_UP_FAILED
 * - SIGN_UP_REQUESTED
 * - SIGN_UP_SUCCEEDED

 * Action Creators:
 * - accountFetchFailed
 * - accountFetchSucceeded
 * - closeAccountDrawer
 * - login
 * - loginFailed
 * - loginSucceeded
 * - logout
 * - openAccountDrawer
 * - requestAccount
 * - resetPassword
 * - resetPasswordFailed
 * - resetPasswordSucceeded
 * - signUpFailed
 * - signUpRequest
 * - signUpSucceeded
 */
import get from 'lodash/get';
import {
  LOG_IN_MODAL_OPENED,
  LOG_IN_MODAL_CLOSED,
  SIGN_UP_MODAL_OPENED,
  SIGN_UP_MODAL_CLOSED,
  FORGOT_PASSWORD_MODAL_OPENED,
  FORGOT_PASSWORD_MODAL_CLOSED,
} from './modals';

import type {
  Account,
  AccountAction,
  AccountDrawerClosed,
  AccountDrawerOpened,
  AccountFetch,
  AccountFetchFailed,
  AccountFetchSucceeded,
  AccountState,
  AuthFailedOpts,
  FacebookLogInSucceeded,
  FacebookSignUpSucceeded,
  GoogleLogInSucceeded,
  GoogleSignUpSucceeded,
  LoginFailed,
  LoginOpts,
  LoginRequest,
  LoginSucceeded,
  LogoutRequest,
  PersistReducerConfig,
  ResetPasswordFailed,
  ResetPasswordRequest,
  ResetPasswordSucceeded,
  SignUpFailed,
  SignUpOpts,
  SignUpRequest,
  SignUpSucceeded,
} from '../types';

import { STORED_STATE_RETRIEVED } from './app';

/** @type {ActionType} */
export const ACCOUNT_FETCH_FAILED = 'PB/account/ACCOUNT_FETCH_FAILED';

/** @type {ActionType} */
export const ACCOUNT_FETCH_REQUESTED = 'PB/account/ACCOUNT_FETCH_REQUESTED';

/** @type {ActionType} */
export const ACCOUNT_FETCH_SUCCEEDED = 'PB/account/ACCOUNT_FETCH_SUCCEEDED';

/**
 * These four actions do not have action creators
 */

/** @type {ActionType} */
export const FACEBOOK_SIGN_UP_SUCCEEDED =
  'PB/account/FACEBOOK_SIGN_UP_SUCCEEDED';

/** @type {ActionType} */
export const FACEBOOK_LOG_IN_SUCCEEDED = 'PB/account/FACEBOOK_LOG_IN_SUCCEEDED';

/** @type {ActionType} */
export const GOOGLE_SIGN_UP_SUCCEEDED = 'PB/account/GOOGLE_SIGN_UP_SUCCEEDED';

/** @type {ActionType} */
export const GOOGLE_LOG_IN_SUCCEEDED = 'PB/account/GOOGLE_LOG_IN_SUCCEEDED';

/** @type {ActionType} */
export const LOG_IN_REQUESTED = 'PB/account/LOG_IN_REQUESTED';

/** @type {ActionType} */
export const LOG_OUT_REQUESTED = 'PB/account/LOG_OUT_REQUESTED';

/** @type {ActionType} */
export const LOG_IN_FAILED = 'PB/account/LOG_IN_FAILED';

/** @type {ActionType} */
export const SIGN_UP_REQUESTED = 'PB/account/SIGN_UP_REQUESTED';

/** @type {ActionType} */
export const SIGN_UP_SUCCEEDED = 'PB/account/SIGN_UP_SUCCEEDED';

/** @type {ActionType} */
export const SIGN_UP_FAILED = 'PB/account/SIGN_UP_FAILED';

/** @type {ActionType} */
export const LOG_IN_SUCCEEDED = 'PB/account/LOG_IN_SUCCEEDED';

/** @type {ActionType} */
export const ACCOUNT_DRAWER_OPENED = 'PB/account/ACCOUNT_DRAWER_OPENED';

/** @type {ActionType} */
export const ACCOUNT_DRAWER_CLOSED = 'PB/account/ACCOUNT_DRAWER_CLOSED';

/** @type {ActionType} */
export const RESET_PASSWORD_FAILED = 'PB/account/RESET_PASSWORD_FAILED';

/** @type {ActionType} */
export const RESET_PASSWORD_REQUESTED = 'PB/account/RESET_PASSWORD_REQUESTED';

/** @type {ActionType} */
export const RESET_PASSWORD_SUCCEEDED = 'PB/account/RESET_PASSWORD_SUCCEEDED';

export const initialState = {
  data: {},
  drawerOpen: false,
  errorMessage: '',
  errorType: '',
  successMessage: '',
  hasSeenSignupIncentive: false,
  loading: false,
  socialSignup: false,
};

/** @ignore */
export default function account(
  state: AccountState = initialState,
  action: AccountAction,
) {
  switch (action.type) {
    case STORED_STATE_RETRIEVED:
      const dehydratedProps = get(action, 'payload.account', {});
      return { ...state, ...dehydratedProps };

    case ACCOUNT_FETCH_REQUESTED:
    case LOG_IN_REQUESTED:
    case SIGN_UP_REQUESTED:
      return { ...state, loading: true };

    case ACCOUNT_FETCH_FAILED:
      return { ...state, loading: false };

    case LOG_IN_FAILED:
    case SIGN_UP_FAILED:
      const message = get(action, 'payload.message', '');
      const errorType = get(action, 'payload.errorType', '');
      return {
        ...state,
        loading: false,
        errorMessage: message,
        errorType,
        successMessage: '',
      };

    // we persist hasSeenSignupIncentive, so we can't just return
    // initial state - also, strictly speaking we should be passing
    // a copy of initial state anyway
    case LOG_OUT_REQUESTED:
      const { hasSeenSignupIncentive } = state;
      return { ...initialState, hasSeenSignupIncentive };

    // ACCOUNT_FETCH_SUCCEEDED is a semi edge case, but if someone already has logged
    // in they should never see the signup incentive
    // so if they have an active session we just set hasSeenSignupIncentive to true
    case ACCOUNT_FETCH_SUCCEEDED:
    case LOG_IN_SUCCEEDED:
    case SIGN_UP_SUCCEEDED:
      const account = get(action, 'payload.account', {});
      const data = account.id && account.id !== -1 ? account : state.data;

      return {
        ...state,
        loading: false,
        errorMessage: '',
        errorType: '',
        successMessage: '',
        hasSeenSignupIncentive: true,
        data,
      };

    case ACCOUNT_DRAWER_OPENED:
      return {
        ...state,
        hasSeenSignupIncentive: true,
        drawerOpen: true,
      };

    case ACCOUNT_DRAWER_CLOSED:
      return { ...state, drawerOpen: false };

    case LOG_IN_MODAL_OPENED:
    case SIGN_UP_MODAL_OPENED:
    case FORGOT_PASSWORD_MODAL_OPENED:
    case FORGOT_PASSWORD_MODAL_CLOSED:
    case LOG_IN_MODAL_CLOSED:
    case SIGN_UP_MODAL_CLOSED:
      return {
        ...state,
        drawerOpen: false,
        errorMessage: '',
        errorType: '',
        hasSeenSignupIncentive: true,
        successMessage: '',
      };

    case RESET_PASSWORD_FAILED:
      const errorMessage = get(action, 'payload.message', '');
      return { ...state, errorMessage };

    case RESET_PASSWORD_SUCCEEDED:
      const successMessage = get(action, 'payload.message', '');
      return { ...state, successMessage };

    case FACEBOOK_LOG_IN_SUCCEEDED:
    case FACEBOOK_SIGN_UP_SUCCEEDED:
    case GOOGLE_LOG_IN_SUCCEEDED:
    case GOOGLE_SIGN_UP_SUCCEEDED:
      return {
        ...state,
        socialSignup: true,
      };

    default:
      return state;
  }
}

/**
 * This action creator will return a LOG_IN_REQUESTED action. Will kick off the
 * login epic.
 *
 * @param {Object} opts
 * @param {string} opts.email - An email address
 * @param {string} opts.password - A password
 * @return {Action}
 * @property {string} type LOG_IN_REQUESTED
 * @property {Object} payload Payload containing email and password
 * @property {Object} payload additionalSignUpInfo containing allowMarketing and userAgent
 */
export function login({
  email,
  password,
  additionalSignUpInfo,
}: LoginOpts): LoginRequest {
  return {
    type: LOG_IN_REQUESTED,
    payload: {
      email,
      password,
      additionalSignUpInfo,
    },
  };
}

// This is what will be persisted in the store.
export const persist: PersistReducerConfig = {
  whitelist: ['hasSeenSignupIncentive', 'socialSignup'],
  reducer: 'account',
};

/**
 * This action creator will return a SIGN_UP_REQUESTED action. Will kick off the
 * login epic.
 *
 * @param {Object} opts
 * @param {boolean} opts.allowMarketing - Flag to allow marketing
 * @param {string} opts.email - An email address
 * @param {string} opts.password - A password
 * @param {string} opts.userAgent - Users agent
 * @return {Action}
 * @property {string} type SIGN_UP_REQUESTED
 * @property {Object} payload Payload containing email and password
 */
export function signup({
  allowMarketing,
  email,
  password,
  userAgent,
}: SignUpOpts): SignUpRequest {
  return {
    type: SIGN_UP_REQUESTED,
    payload: {
      allowMarketing,
      email,
      password,
      userAgent,
    },
  };
}

/**
 * This action creator will return a LOG_IN_FAILED action given an error
 * message.
 *
 * @param {string} message - A message summarizing the error. May be shown to
 *     the user (So watch yr language).
 * @return {Action}
 * @property {string} type LOG_IN_FAILED
 * @property {Object} payload The payload is an object containing the message, and error type
 *     e.g.`{ message: '', errorType: '' }`
 */
export function loginFailed({
  message,
  errorType,
}: AuthFailedOpts): LoginFailed {
  return {
    type: LOG_IN_FAILED,
    payload: {
      message,
      errorType,
    },
  };
}

/**
 * This action creator will return a SIGN_UP_FAILED action.
 *
 * @return {Action}
 * @property {string} type SIGN_UP_FAILED
 * @property {Object} payload The payload is an object containing the message, and error type
 *     e.g.`{ message: '', errorType: '' }`
 */
export function signUpFailed({
  message,
  errorType,
}: AuthFailedOpts): SignUpFailed {
  return {
    type: SIGN_UP_FAILED,
    payload: {
      message,
      errorType,
    },
  };
}

/**
 * This action creator will return a LOG_IN_SUCCEEDED action.
 *
 * @param {Object} opts
 * @param {string} opts.displayName - The user's display name
 * @param {string} opts.email - An email address
 * @param {number} opts.id - The user's id
 * @return {Action}
 * @property {string} type LOG_IN_SUCCEEDED
 * @property {Object} payload Payload containing displayName, email, and id
 */
export function loginSucceeded(
  { coins, currentCartId, displayName, email, id, isConfirmed }: Account,
  method: string,
): LoginSucceeded {
  return {
    type: LOG_IN_SUCCEEDED,
    payload: {
      account: {
        coins,
        currentCartId,
        displayName,
        email,
        id,
        isConfirmed,
      },
      method: method || 'email',
    },
  };
}

/**
 * This action creator will return a SIGN_UP_SUCCEEDED action.
 *
 * @param {Object} opts
 * @param {string} opts.displayName - The user's display name
 * @param {string} opts.email - An email address
 * @param {number} opts.id - The user's id
 * @return {Action}
 * @property {string} type SIGN_UP_SUCCEEDED
 * @property {Object} payload Payload containing displayName, email, and id
 */
export function signUpSucceeded(
  { coins, currentCartId, displayName, email, id, isConfirmed }: Account,
  method: string,
): SignUpSucceeded {
  return {
    type: SIGN_UP_SUCCEEDED,
    payload: {
      account: {
        coins,
        currentCartId,
        displayName,
        email,
        id,
        isConfirmed,
      },
      method: method || 'email',
    },
  };
}

/**
 * This action creator will return a ACCOUNT_FETCH_SUCCEEDED action.
 *
 * @param {Object} opts
 * @param {string} opts.displayName - The user's display name
 * @param {string} opts.email - An email address
 * @param {number} opts.id - The user's id
 * @return {Action}
 * @property {string} type ACCOUNT_FETCH_SUCCEEDED
 * @property {Object} payload Payload containing displayName, email, and id
 */
export function accountFetchSucceeded({
  coins,
  currentCartId,
  displayName,
  email,
  id,
  isConfirmed,
}: Account): AccountFetchSucceeded {
  return {
    type: ACCOUNT_FETCH_SUCCEEDED,
    payload: {
      account: {
        coins,
        currentCartId,
        displayName,
        email,
        id,
        isConfirmed,
      },
    },
  };
}

/**
 * This action creator will return a LOG_OUT_REQUESTED action.
 *
 * @return {Action}
 * @property {string} type LOG_OUT_REQUESTED
 */
export function logout(): LogoutRequest {
  return { type: LOG_OUT_REQUESTED };
}

export function facebookLoginSucceded(
  account: Account,
): FacebookLogInSucceeded {
  return { type: FACEBOOK_LOG_IN_SUCCEEDED, payload: { account } };
}

export function facebookSignUpSucceded(
  account: Account,
): FacebookSignUpSucceeded {
  return { type: FACEBOOK_SIGN_UP_SUCCEEDED, payload: { account } };
}

export function googleLoginSucceeded(account: Account): GoogleLogInSucceeded {
  return { type: GOOGLE_LOG_IN_SUCCEEDED, payload: { account } };
}

export function googleSignUpSucceded(account: Account): GoogleSignUpSucceeded {
  return { type: GOOGLE_SIGN_UP_SUCCEEDED, payload: { account } };
}

/**
 * This action creator will return a ACCOUNT_FETCH_REQUESTED action.
 *
 * @return {Action}
 * @property {string} type ACCOUNT_FETCH_REQUESTED
 */
export function requestAccount(): AccountFetch {
  return { type: ACCOUNT_FETCH_REQUESTED };
}

/**
 * This action creator will return a ACCOUNT_FETCH_FAILED action.
 *
 * @return {Action}
 * @property {string} type ACCOUNT_FETCH_FAILED
 */
export function accountFetchFailed(): AccountFetchFailed {
  return { type: ACCOUNT_FETCH_FAILED };
}

/**
 * This action creator will return a ACCOUNT_DRAWER_OPENED action.
 *
 * @return {Action}
 * @property {string} type ACCOUNT_DRAWER_OPENED
 */
export function openAccountDrawer(): AccountDrawerOpened {
  return { type: ACCOUNT_DRAWER_OPENED };
}

/**
 * This action creator will return a ACCOUNT_DRAWER_CLOSED action.
 *
 * @return {Action}
 * @property {string} type ACCOUNT_DRAWER_CLOSED
 */
export function closeAccountDrawer(): AccountDrawerClosed {
  return { type: ACCOUNT_DRAWER_CLOSED };
}

/**
 * This action creator will return a RESET_PASSWORD_REQUESTED action. Takes
 * an email address to reset the password for.
 *
 * @param {string} email - the user's email address to request resetting
 * @return {Action}
 * @property {string} type RESET_PASSWORD_REQUESTED
 */
export function resetPassword(email: string): ResetPasswordRequest {
  return {
    type: RESET_PASSWORD_REQUESTED,
    payload: { email },
  };
}

/**
 * This action creator will return a RESET_PASSWORD_SUCCEEDED action.
 *
 * @return {Action}
 * @property {string} type RESET_PASSWORD_SUCCEEDED
 * @property {Object} payload Payload containing a message
 */
export function resetPasswordSucceeded(): ResetPasswordSucceeded {
  return {
    type: RESET_PASSWORD_SUCCEEDED,
    payload: {
      message:
        'If an account exists for your email, we will email you instructions for resetting your password within ten minutes. If you do not receive a password reset email, please contact help@paperlesspost.com',
    },
  };
}

/**
 * This action creator will return a RESET_PASSWORD_SUCCEEDED action.
 *
 * @return {Action}
 * @property {string} type RESET_PASSWORD_SUCCEEDED
 * @property {Object} payload Payload containing a message
 */
export function resetPasswordFailed(): ResetPasswordFailed {
  return {
    type: RESET_PASSWORD_FAILED,
    payload: {
      message:
        'There was a problem resetting your password, please try again or reach out to help@paperlesspost.com',
    },
  };
}
